import { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box, Title, Text, Stack, Table, Select, Checkbox, Badge,
  Loader, Center, Alert, Group, TextInput, UnstyledButton,
} from '@mantine/core';
import { IconAlertCircle, IconCheck } from '@tabler/icons-react';
import { api } from '../apiClient';

const STATUS_OPTIONS = [
  { value: '', label: '未設定' },
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'learning', label: 'Learning' },
  { value: 'certified', label: 'Certified' },
  { value: 'retired', label: 'Retired' },
];

const VENDOR_LABEL = { aws: 'AWS', microsoft: 'Microsoft', google: 'Google' };
const VENDOR_COLOR = { aws: '#e8590c', microsoft: '#1c7ed6', google: '#34A853' };
const VENDOR_HEX   = { aws: '#e8590c', microsoft: '#1c7ed6', google: '#34A853' };

function VendorPill({ label, color, active, onClick }) {
  return (
    <UnstyledButton
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 18px',
        borderRadius: 999,
        fontWeight: 600,
        fontSize: 14,
        cursor: 'pointer',
        transition: 'all 0.18s ease',
        border: `2px solid ${color}`,
        background: active ? color : 'transparent',
        color: active ? '#fff' : color,
        boxShadow: active ? `0 2px 12px ${color}55` : 'none',
        letterSpacing: '0.02em',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: active ? '#fff' : color,
          flexShrink: 0,
          transition: 'background 0.18s ease',
        }}
      />
      {label}
    </UnstyledButton>
  );
}

function Certifications() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [selectedVendor, setSelectedVendor] = useState('all');
  const timerRefs = useRef({});

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get('/user-certifications/all-statuses')
      .then((data) => setCerts(data ?? []))
      .catch(() => setError('データの取得に失敗しました。'))
      .finally(() => setLoading(false));
  }, []);

  const certsRef = useRef(certs);
  useEffect(() => { certsRef.current = certs; }, [certs]);

  const handleChange = (certId, field, value) => {
    setCerts((prev) =>
      prev.map((c) => c.id === certId ? { ...c, [field]: value } : c)
    );

    // デバウンス保存（0.8秒）
    if (timerRefs.current[certId]) clearTimeout(timerRefs.current[certId]);
    timerRefs.current[certId] = setTimeout(async () => {
      const cert = certsRef.current.find((c) => c.id === certId);
      if (!cert) return;
      const updated = { ...cert, [field]: value };
      setSaving((prev) => ({ ...prev, [certId]: true }));
      setSaved((prev) => ({ ...prev, [certId]: false }));
      try {
        await api.put('/user-certification-details', {
          certificationId: certId,
          status: updated.status || null,
          acquired_at: updated.acquired_at || null,
          expires_at: updated.expires_at || null,
          show_on_home: updated.show_on_home !== false,
        });
        setSaved((prev) => ({ ...prev, [certId]: true }));
        setTimeout(() => setSaved((prev) => ({ ...prev, [certId]: false })), 2000);
      } catch {
        setError('保存に失敗しました。');
      } finally {
        setSaving((prev) => ({ ...prev, [certId]: false }));
      }
    }, 800);
  };

  const byVendor = useMemo(() => certs.reduce((acc, c) => {
    const v = (c.vendor ?? 'other').toLowerCase();
    if (!acc[v]) acc[v] = [];
    acc[v].push(c);
    return acc;
  }, {}), [certs]);

  const vendorKeys = useMemo(() => Object.keys(byVendor), [byVendor]);

  const visibleVendors = selectedVendor === 'all'
    ? vendorKeys
    : vendorKeys.filter((v) => v === selectedVendor);

  if (loading) return <Center h={200}><Loader /></Center>;

  return (
    <Box p="xl">
      <Group justify="space-between" align="flex-end" mb="lg" wrap="wrap" gap="md">
        <Title order={2}>My Certifications</Title>

        {/* ベンダーセレクター */}
        <Group gap="sm">
          <VendorPill
            label="All"
            color="#868e96"
            active={selectedVendor === 'all'}
            onClick={() => setSelectedVendor('all')}
          />
          {vendorKeys.map((v) => (
            <VendorPill
              key={v}
              label={VENDOR_LABEL[v] ?? v}
              color={VENDOR_HEX[v] ?? '#868e96'}
              active={selectedVendor === v}
              onClick={() => setSelectedVendor(v)}
            />
          ))}
        </Group>
      </Group>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" mb="md" onClose={() => setError(null)} withCloseButton>
          {error}
        </Alert>
      )}

      <Stack gap="xl">
        {visibleVendors.map((vendor) => {
          const vendorCerts = byVendor[vendor];
          return (
          <Box key={vendor}>
            <Group mb="xs" gap="xs">
              <Badge color={VENDOR_COLOR[vendor] ?? 'gray'} variant="filled" size="lg" style={{ textTransform: 'none' }}>
                {VENDOR_LABEL[vendor] ?? vendor}
              </Badge>
            </Group>
            <Table withTableBorder withColumnBorders striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ minWidth: 200 }}>資格名</Table.Th>
                  <Table.Th style={{ minWidth: 130 }}>ステータス</Table.Th>
                  <Table.Th style={{ minWidth: 150 }}>取得日</Table.Th>
                  <Table.Th style={{ minWidth: 150 }}>有効期限</Table.Th>
                  <Table.Th style={{ minWidth: 120 }}>ホームに表示</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {vendorCerts.map((c) => (
                  <Table.Tr key={c.id}>
                    <Table.Td>
                      <Text size="sm" fw={500}>{c.formal_name ?? c.name}</Text>
                      <Text size="xs" c="dimmed" ff="monospace">{c.code?.toUpperCase()}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Select
                        size="xs"
                        data={STATUS_OPTIONS}
                        value={c.status ?? ''}
                        onChange={(val) => handleChange(c.id, 'status', val)}
                        disabled={saving[c.id]}
                        styles={{ input: { minWidth: 110 } }}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        type="date"
                        size="xs"
                        value={c.acquired_at ? c.acquired_at.slice(0, 10) : ''}
                        onChange={(e) => handleChange(c.id, 'acquired_at', e.target.value || null)}
                        disabled={saving[c.id]}
                      />
                    </Table.Td>
                    <Table.Td>
                      <TextInput
                        type="date"
                        size="xs"
                        value={c.expires_at ? c.expires_at.slice(0, 10) : ''}
                        onChange={(e) => handleChange(c.id, 'expires_at', e.target.value || null)}
                        disabled={saving[c.id]}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" align="center">
                        <Checkbox
                          checked={c.show_on_home !== false}
                          onChange={(e) => handleChange(c.id, 'show_on_home', e.target.checked)}
                          disabled={saving[c.id]}
                          label="表示"
                          size="sm"
                        />
                        {saving[c.id] && <Loader size="xs" />}
                        {saved[c.id] && <IconCheck size={16} color="green" />}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

export default Certifications;
